import { NotesListView } from '../view/NotesListView';
import { templateIncidentNotesPanel } from '../view/template';
import { NotesListRepository } from '../repository/NotesListRepository';
import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../framework/BaseWebComponentController';
import { NotesListWebComponent } from '../index';
import { NoteDisplayMode } from '../../noteEditor/types';
import { ToastPositionX, ToastPositionY, ToastSeverity } from '../../../ui/gzToastNotification/type';
import { GzToastNotificationWebComponent } from '../../../ui/gzToastNotification';
import { NoteEvents, NotesListViewData } from '../types';

export class NotesListController extends BaseWebComponentController {
  public component: NotesListWebComponent;
  public dataRepository: NotesListRepository;
  public view: NotesListView;
  public viewConstructor: typeof NotesListView;
  public dataRepositoryConstructor: typeof NotesListRepository;

  constructor(component: NotesListWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: NotesListView,
      viewTemplate: templateIncidentNotesPanel,
      dataRepositoryConstructor: NotesListRepository,
    });
  }

  public computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$backButton,
        event: 'click',
        callback: this.goBack,
      },
      {
        element: this.view.dom.$emptyStateAddNoteButton,
        event: 'click',
        callback: this.showNoteEditorInCreateMode,
      },
      {
        element: this.view.dom.$addNoteButton,
        event: 'click',
        callback: this.showNoteEditorInCreateMode,
      },
      {
        element: this.view.dom.$notesListContainer,
        event: NoteEvents.CREATE_NOTE,
        callback: this.createNote,
      },
      {
        element: this.view.dom.$notesListContainer,
        event: NoteEvents.EDIT_NOTE_CARD,
        callback: this.showNoteEditorInEditMode,
      },
      {
        element: this.view.dom.$notesListContainer,
        event: NoteEvents.EDIT_NOTE,
        callback: this.editNote,
      },
      {
        element: this.view.dom.$notesListContainer,
        event: NoteEvents.DELETE_NOTE_CARD,
        callback: this.showNoteEditorInDeleteMode,
      },
      {
        element: this.view.dom.$notesListContainer,
        event: NoteEvents.DELETE_NOTE,
        callback: this.deleteNote,
      },
      {
        element: this.view.dom.$notesListContainer,
        event: NoteEvents.CLOSE_EDIT_WINDOW,
        callback: this.closeNoteEditor,
      },
    ];
  }

  public async getDataForView(): Promise<NotesListViewData> {
    this.view.setLoadingState();

    try {
      return {
        isBackButtonEnabled: this.dependencies.isBackButtonEnabled ?? false,
        notes: await this.dataRepository.getNotes(),
      };
    } finally {
      this.view.removeLoadingState();
    }
  }

  private goBack(): void {
    this.dispatchComponentEvent(NoteEvents.GO_BACK);
  }

  private dispatchComponentEvent(event: NoteEvents, detail?: any): void {
    this.component.dispatchEvent(
      new CustomEvent(event, { ...(detail ? { detail } : {}), bubbles: true, cancelable: true, composed: false })
    );
  }

  private async showNoteEditorInCreateMode(): Promise<void> {
    await this.view.showNoteEditor(null, NoteDisplayMode.EDIT);

    this.view.toggleOverlayVisibility(true);

    this.dispatchComponentEvent(NoteEvents.CREATE_NOTE_CARD);
  }

  private async createNote(event: WebComponentEvent): Promise<void> {
    try {
      await this.dataRepository.createNote(event.detail.noteRequest);

      this.forceRender();

      this.createToastNotification(this.dependencies.translateFn('Note created.'), ToastSeverity.SUCCESS);

      this.dispatchComponentEvent(NoteEvents.CREATE_NOTE, event.detail);
    } catch (error: any) {
      this.createToastNotification(this.dependencies.translateFn('Failed to create the note.'), ToastSeverity.ERROR);
    }
  }

  private forceRender(): void {
    this.component.propertiesSet = false;

    this.view.removeNoteEditor();
    this.view.removeNotes();
    this.view.toggleOverlayVisibility(false);

    this.component.propertiesSet = true;
  }

  private createToastNotification(message: string, severity: ToastSeverity): void {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: message,
      hasAction: false,
      severity: severity,
      x: ToastPositionX.RIGHT,
      y: ToastPositionY.BOTTOM,
    });
  }

  private async showNoteEditorInEditMode(event: WebComponentEvent): Promise<void> {
    await this.view.showNoteEditor(event.detail.noteRequest, NoteDisplayMode.EDIT);

    this.view.toggleOverlayVisibility(true);

    this.dispatchComponentEvent(NoteEvents.EDIT_NOTE_CARD, event.detail);
  }

  private async editNote(event: WebComponentEvent): Promise<void> {
    try {
      await this.dataRepository.updateNote(event.detail.noteRequest);

      this.forceRender();

      this.createToastNotification(this.dependencies.translateFn('Note edited.'), ToastSeverity.SUCCESS);

      this.dispatchComponentEvent(NoteEvents.EDIT_NOTE, event.detail);
    } catch (error: any) {
      this.createToastNotification(this.dependencies.translateFn('Failed to edit the note.'), ToastSeverity.ERROR);
    }
  }

  private async showNoteEditorInDeleteMode(event: WebComponentEvent): Promise<void> {
    await this.view.showNoteEditor(event.detail.noteRequest, NoteDisplayMode.DELETE);

    this.view.toggleOverlayVisibility(true);

    this.dispatchComponentEvent(NoteEvents.DELETE_NOTE_CARD, event.detail);
  }

  private async deleteNote(event: WebComponentEvent): Promise<void> {
    try {
      await this.dataRepository.deleteNote(event.detail.noteRequest);

      this.forceRender();

      this.createToastNotification(this.dependencies.translateFn('Note deleted.'), ToastSeverity.SUCCESS);

      this.dispatchComponentEvent(NoteEvents.DELETE_NOTE, event.detail);
    } catch (error: any) {
      this.createToastNotification(this.dependencies.translateFn('Could not delete note.'), ToastSeverity.ERROR);
    }
  }

  private closeNoteEditor(): void {
    this.view.toggleOverlayVisibility(false);

    this.dispatchComponentEvent(NoteEvents.CLOSE_EDIT_WINDOW);
  }
}
